// @flow
import isFunction from 'lodash-es/isFunction';
import mergeWith from 'lodash-es/mergeWith';
import isPlainObject from 'lodash-es/isPlainObject';
import mapValues from 'lodash-es/mapValues';
import forEach from 'lodash-es/forEach';
import transform from 'lodash-es/transform';

/**
 * This function creates an initial validationErrors
 * object with the appropriate shape. The result will
 * be in "valid" state meaning all leaf values are null.
 *
 * input:
 * {
 *   name: {
 *      first: Form.validators.required,
 *      last: Form.validators.required,
 *   },
 *   email: [Form.validators.required, Form.validators.minLength(3)],
 * }
 *
 * output:
 * {
 *   name: {
 *      first: null,
 *      last: null,
 *   },
 *   email: null,
 * }
 * @param {object} validators
 */
export const initializeValidation = (intialData: Object, validators: *) => {
  const resolvedValidators = isFunction(validators)
    ? // $FlowFixMe(Ryan) flow doesn't understand type refinement from isFunction
      validators(intialData)
    : validators;
  return mergeWith({}, resolvedValidators, resolvedValidators, (a, b) => {
    if (!isPlainObject(b)) {
      return null;
    }
    return undefined;
  });
};

/**
 * Validators can be optionally configured. If they
 * haven't been configured in any way, this function
 * will resolve them with default params.
 *
 * @param {function} validator
 */
export const resolveValidator = (validator: Function) => {
  const testValue = validator();
  return isFunction(testValue) ? testValue : validator;
};

/**
 * This function takes two objects of the same shape:
 * the first contains user input, the other the validator
 * functions. It returns an object of the same shape with
 * error messages (or null) as values.
 *
 * input:
 * {
 *   name: {
 *      first: 'John',
 *      last: '',
 *   },
 *   email: 'jo',
 * },
 * {
 *   name: {
 *      first: Form.validators.required,
 *      last: Form.validators.required,
 *   },
 *   email: [Form.validators.required, Form.validators.minLength(3)],
 * }
 *
 * output:
 * {
 *   name: {
 *      first: null,
 *      last: 'This field is required.',
 *   },
 *   email: 'This field must be at least 3 characters.',
 * }
 * @param {object} data
 * @param {object} validators
 */
export const getErrors = (data: Object, validators: *) =>
  mapValues(validators, (value, key) => {
    // Recurse into nested objects
    if (isPlainObject(value)) {
      return getErrors(data[key], value);
    }
    // Assure validators are in an array
    const validatorList = Array.isArray(value) ? value : [value];

    // Run validators serially on data value,
    // terminating early if error is found
    let error = null;
    forEach(validatorList, validator => {
      error = resolveValidator(validator)(data[key]);
      return error === null;
    });
    return error;
  });

/**
 * This function checks to see if there are any non-null
 * values (representing errors) in the object.
 *
 * @param {object} errors
 */
export const isValid = (errors: Object) =>
  transform(
    errors,
    (result, value) => {
      if (value !== null && isPlainObject(value)) {
        result.valid = isValid(value).valid; // eslint-disable-line no-param-reassign
      } else {
        result.valid = value === null; // eslint-disable-line no-param-reassign
      }
      return result.valid;
    },
    { valid: true }
  );

export const validate = (data: Object, validators: *) => {
  const resolvedValidators = isFunction(validators)
    ? // $FlowFixMe(Ryan) flow doesn't understand type refinement from isFunction
      validators(data)
    : validators;
  const errors = getErrors(data, resolvedValidators);
  return { ...isValid(errors), errors };
};



// WEBPACK FOOTER //
// ../react-lib/src/components/utils/Form/validation/index.js